import { ZipAvailabilityProbabilityEnum } from '@inter-ikea-kompis/enums';
import type { StoreAvailabilityProbabilityEnum } from '@inter-ikea-kompis/enums';
import type { IStoreAvailability, IZipAvailability } from '@inter-ikea-kompis/types';
import { AvailabilityUtility } from '@inter-ikea-kompis/utilities';

import StatusIndicatorColorEnum from '../enums/StatusIndicatorColorEnum.js';
import type IStatusIndicatorLocalizedInformation from '../types/IStatusIndicatorLocalizedInformation.js';

/**
 * Status indicator utility.
 *
 * @deprecated Use ZipAvailabilityUtility or AvailabilityUtility in the utilities package instead.
 */
export default class StatusIndicatorUtility {
	/**
	 * Returns status indicator enum.
	 *
	 * @param storeAvailability Store availability.
	 * @returns StoreAvailabilityProbabilityEnum | null.
	 */
	public static getStoreAvailabilty(
		storeAvailability: IStoreAvailability | IStoreAvailability[] // Prop name and type not to introduce a breaking change from originally only supporting one availabiltity
	): StoreAvailabilityProbabilityEnum | null {
		if (Array.isArray(storeAvailability)) {
			return AvailabilityUtility.getStoreAvailabilityStatus(storeAvailability);
		}
		return AvailabilityUtility.getStoreAvailabilityStatus([storeAvailability]);
	}

	/**
	 * Returns status indicator label.
	 *
	 * @param options Options.
	 * @param options.localizedInformation Localized information.
	 * @param options.storeAvailability Store availability.
	 * @returns Status indicator label.
	 */
	public static getStoreAvailabilityLabel(options: {
		localizedInformation: IStatusIndicatorLocalizedInformation;
		storeAvailability: IStoreAvailability | IStoreAvailability[];
	}): string | null {
		const status = this.getStoreAvailabilty(options.storeAvailability);
		return AvailabilityUtility.getStoreAvailabilityTranslation(
			status,
			options.localizedInformation.translations
		);
	}

	/**
	 * Returns status indicator color.
	 *
	 * @param storeAvailability Store availability.
	 * @returns Status indicator color.
	 */
	public static getStoreAvailabilityColor(
		storeAvailability: IStoreAvailability | IStoreAvailability[]
	): StatusIndicatorColorEnum | null {
		const availability = this.getStoreAvailabilty(storeAvailability);
		if (availability) {
			return <StatusIndicatorColorEnum | null>(
				AvailabilityUtility.getStoreAvailabilityColor(availability)
			);
		}
		return null;
	}

	/**
	 * Returns status indicator label for zip.
	 *
	 * @param options Options.
	 * @param options.localizedInformation Localized information.
	 * @param options.zipAvailability Zip availability.
	 * @returns Status Indicator Label.
	 */
	public static getZipAvailabilityLabel(options: {
		localizedInformation: IStatusIndicatorLocalizedInformation;
		zipAvailability: IZipAvailability | IZipAvailability[] | null;
	}): string {
		const { zipAvailability, localizedInformation } = options;
		const { highInStock, lowInStock, mediumInStock, outOfStock, notSoldTemp, notInRange } =
			ZipAvailabilityProbabilityEnum;

		if (!Array.isArray(zipAvailability)) {
			if (!zipAvailability) {
				return options.localizedInformation.translations.zipAvailabilityNoDeliveryInfo;
			}

			if (zipAvailability?.content?.availableForHomeDelivery) {
				return options.localizedInformation.translations.zipAvailabilityAvailable;
			} else {
				switch (zipAvailability?.content?.homeDelivery?.messageType) {
					case outOfStock:
						return localizedInformation.translations.zipAvailabilityUnavailable;
					case notSoldTemp:
						return localizedInformation.translations.zipAvailabilityCurrentlyNotSold;
					case notInRange:
						return localizedInformation.translations.zipAvailabilityNotSold;
					default:
						return localizedInformation.translations.zipAvailabilityNoDeliveryInfo;
				}
			}
		} else {
			const zipAvailabilities = zipAvailability
				.map((item) => item.content.homeDelivery?.messageType)
				.filter((type) => type != null);

			if (zipAvailabilities.every((type) => type == highInStock)) {
				return localizedInformation.translations.zipAvailabilityAvailable;
			}
			if (zipAvailabilities.every((type) => type == outOfStock)) {
				return localizedInformation.translations.zipAvailabilityUnavailable;
			} else if (
				zipAvailabilities.some(
					(type) => type == lowInStock || type == mediumInStock || type == outOfStock
				)
			) {
				return localizedInformation.translations.homeDeliveryStatusSomeAvailableWithoutZip;
			}
		}
		return '';
	}

	/**
	 * Returns status indicator color for zip.
	 *
	 * @returns Status Indicator Color.
	 */
	public static getZipAvailabilityColor(options: {
		zipAvailability: IZipAvailability | IZipAvailability[] | null;
	}): StatusIndicatorColorEnum | null {
		const { zipAvailability } = options;
		const { highInStock, lowInStock, mediumInStock, outOfStock } = ZipAvailabilityProbabilityEnum;

		if (!Array.isArray(zipAvailability)) {
			if (!zipAvailability) {
				return StatusIndicatorColorEnum.grey;
			}

			if (zipAvailability?.content?.availableForHomeDelivery) {
				return StatusIndicatorColorEnum.green;
			} else {
				switch (zipAvailability.content?.homeDelivery?.messageType) {
					case ZipAvailabilityProbabilityEnum.outOfStock:
					case ZipAvailabilityProbabilityEnum.notSoldTemp:
						return StatusIndicatorColorEnum.red;
					default:
						return StatusIndicatorColorEnum.grey;
				}
			}
		} else {
			const zipAvailabilities = zipAvailability
				.map((item) => item.content.homeDelivery?.messageType)
				.filter((type) => type != null);

			if (zipAvailabilities.every((type) => type == highInStock)) {
				return StatusIndicatorColorEnum.green;
			}
			if (zipAvailabilities.every((type) => type == outOfStock)) {
				return StatusIndicatorColorEnum.red;
			} else if (
				zipAvailabilities.some(
					(type) => type == lowInStock || type == mediumInStock || type == outOfStock
				)
			) {
				return StatusIndicatorColorEnum.orange;
			}
		}
		return null;
	}
}
